// Client-side image optimizer: resizes and compresses images before upload.
const MAX_SERVER_DIMENSION = 4096; // backend limit (given)
const TARGET_CLIENT_DIMENSION = 1536; // target for mobile display to save storage

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function dataUrlToImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

export async function optimizeImageFile(file, options = {}) {
  const maxDim = Math.min(
    MAX_SERVER_DIMENSION,
    options.maxDimension || TARGET_CLIENT_DIMENSION,
  );
  const quality = options.quality || 0.8;
  const fillWhiteBackground = options.fillWhiteBackground || false;

  try {
    const dataUrl = await fileToDataUrl(file);
    const img = await dataUrlToImage(dataUrl);

    const width = img.width;
    const height = img.height;
    
    const needsResize = width > maxDim || height > maxDim;

    // If already within target dimension and no white background needed, return original file
    if (!needsResize && !fillWhiteBackground) {
      return file;
    }

    // Compute target size keeping aspect ratio
    const ratio = width / height;
    let targetWidth = width;
    let targetHeight = height;

    if (needsResize) {
      if (width > height) {
        targetWidth = maxDim;
        targetHeight = Math.round(maxDim / ratio);
      } else {
        targetHeight = maxDim;
        targetWidth = Math.round(maxDim * ratio);
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext("2d");

    if (fillWhiteBackground) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    // Preserve PNG, WEBP, or original mimeType to maintain transparency
    const mimeType = file.type || "image/png";

    const blob = await canvasToBlob(canvas, mimeType, quality);
    if (!blob) return file;

    const extMatch = file.name && file.name.match(/\.(png|jpeg|jpg|gif|webp)$/i);
    const ext = extMatch ? extMatch[0] : (mimeType === "image/png" ? ".png" : ".jpg");
    const newName = file.name
      ? file.name.replace(/\.(png|jpeg|jpg|gif|webp)$/i, ext)
      : `upload${ext}`;
    const optimizedFile = new File([blob], newName, {
      type: blob.type,
    });
    return optimizedFile;
  } catch (err) {
    // On any error, fall back to original file
    console.error("Image optimization failed, uploading original file", err);
    return file;
  }
}

export default optimizeImageFile;
